/**
 * Atlas Ingestion — K2 + K3 admin page (2026-08-05).
 *
 * `/atlas/admin/ingest` — drag-drop upload, list of past uploads,
 * per-upload review panel with candidate rows.
 *
 * All routes admin-only. Ingested facts land with K1 source metadata
 * automatically attached. Nothing writes to `atlas_facts` until the
 * admin clicks Apply and picks per-candidate actions.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, FileText, Loader2, Check as CheckIcon, AlertTriangle,
  Info, RefreshCw, X as CloseIcon, ExternalLink, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// ─────────────────────────────────────────────────────────────
// Root page — routes on `hash` param: list vs single-upload review.
// (Simpler than a nested Route — one file, one page.)
// ─────────────────────────────────────────────────────────────
export default function AtlasIngestPage() {
  const { upload_id } = useParams();
  if (upload_id === "conflicts") return <ConflictsPage />;
  if (upload_id) return <ReviewPanel uploadId={upload_id} />;
  return <UploadsListPage />;
}

// ─────────────────────────────────────────────────────────────
// LIST + UPLOADER
// ─────────────────────────────────────────────────────────────
function UploadsListPage() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/atlas/ingest/uploads?limit=50");
      setUploads(data?.uploads || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/atlas/ingest/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.status === "error") {
        toast.error(data.error || "Extraction failed");
      } else {
        toast.success(`Extracted ${data.entity_count} entities + ${data.fact_count} facts`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setBusy(false); }
  }, [load]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) doUpload(f);
  }, [doUpload]);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6" data-testid="atlas-ingest-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/atlas/dashboard"
          className="inline-flex items-center gap-1 text-tb-navy/60 hover:text-tb-navy text-xs font-hand"
          data-testid="atlas-ingest-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <Link to="/atlas/admin/ingest/conflicts"
          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-amber-300 bg-amber-50 text-amber-800 font-hand text-xs hover:bg-amber-100"
          data-testid="atlas-ingest-conflicts-link">
          <AlertTriangle className="w-3.5 h-3.5" /> Conflicts
        </Link>
      </div>

      <h1 className="text-tb-navy text-3xl mb-1"
          style={{ fontFamily: '"Playfair Display", serif' }}>
        Knowledge Ingestion
      </h1>
      <p className="text-tb-navy/60 font-hand text-sm mb-6">
        Upload a PDF, text file, or CSV — the extractor pulls entities and facts,
        you review them, then apply what should join the graph.
      </p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="atlas-ingest-dropzone"
        className={
          "rounded-2xl border-2 border-dashed p-8 text-center transition-colors " +
          (dragOver
            ? "border-tb-navy bg-tb-navy/5"
            : "border-tb-navy/25 bg-tb-navy/[0.02] hover:border-tb-navy/40")
        }
      >
        <Upload className="w-8 h-8 mx-auto text-tb-navy/50" />
        <div className="mt-2 font-hand text-tb-navy text-sm">
          {busy ? "Extracting…" : "Drag & drop a file here"}
        </div>
        <div className="mt-1 font-hand text-tb-navy/50 text-xs">
          or
        </div>
        <button type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="atlas-ingest-choose-file"
          className="mt-2 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
          {busy
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Upload className="w-4 h-4" />}
          Choose file
        </button>
        <input ref={inputRef}
          type="file"
          accept=".pdf,.txt,.csv,.md,text/plain,text/csv,text/markdown,application/pdf"
          className="hidden"
          data-testid="atlas-ingest-file-input"
          onChange={(e) => doUpload(e.target.files?.[0])} />
        <div className="mt-3 font-hand text-tb-navy/40 text-[11px]">
          Accepted: PDF · TXT · CSV · MD · max 12 MB
        </div>
      </div>

      {/* Uploads list */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-tb-navy text-lg font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Recent uploads
          </h2>
          <button type="button" onClick={load}
            data-testid="atlas-ingest-refresh"
            className="inline-flex items-center gap-1 text-xs font-hand text-tb-navy/60 hover:text-tb-navy">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {loading && <div className="font-hand text-tb-navy/50 text-sm">Loading…</div>}
        {!loading && uploads.length === 0 && (
          <div className="rounded-xl border border-dashed border-tb-navy/20 p-6 text-center font-hand text-tb-navy/50 text-sm">
            No uploads yet.
          </div>
        )}
        {!loading && uploads.length > 0 && (
          <div className="rounded-xl border border-tb-navy/15 overflow-hidden divide-y divide-tb-navy/10 bg-white">
            {uploads.map((u) => <UploadRow key={u.id} u={u} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    processing: "bg-amber-100 text-amber-800 border-amber-300",
    reviewing:  "bg-sky-100 text-sky-800 border-sky-300",
    applied:    "bg-emerald-100 text-emerald-800 border-emerald-300",
    error:      "bg-red-100 text-red-800 border-red-300",
    rejected:   "bg-tb-navy/10 text-tb-navy/70 border-tb-navy/20",
    // 2026-08-06 undo — visually neutral so it doesn't compete with
    // the destructive red used on the Undo apply button.
    undone:     "bg-tb-navy/8 text-tb-navy/60 border-tb-navy/20",
  };
  const cls = map[status] || "bg-tb-navy/10 text-tb-navy/70 border-tb-navy/20";
  return (
    <span data-testid={`atlas-ingest-status-${status}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-hand uppercase tracking-widest ${cls}`}>
      {status}
    </span>
  );
}

function UploadRow({ u }) {
  const nav = useNavigate();
  const uploadedAt = u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : "—";
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 hover:bg-tb-navy/[0.02] cursor-pointer"
      onClick={() => nav(`/atlas/admin/ingest/${u.id}`)}
      data-testid={`atlas-ingest-upload-row-${u.id}`}
    >
      <FileText className="w-5 h-5 text-tb-navy/50 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-hand text-tb-navy text-sm truncate" title={u.filename}>
          {u.filename}
        </div>
        <div className="font-hand text-tb-navy/50 text-[11px]">
          {uploadedAt}
          {(u.status === "reviewing" || u.status === "applied") && (
            <> · {u.entity_count || 0} entities · {u.fact_count || 0} facts</>
          )}
          {u.error && <> · <span className="text-red-700">{u.error}</span></>}
        </div>
      </div>
      <StatusPill status={u.status} />
      <button type="button"
        onClick={(e) => { e.stopPropagation(); nav(`/atlas/admin/ingest/${u.id}`); }}
        className="text-xs font-hand text-tb-navy/70 hover:text-tb-navy"
        data-testid={`atlas-ingest-review-btn-${u.id}`}>
        Review →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// REVIEW PANEL
// ─────────────────────────────────────────────────────────────
function ReviewPanel({ uploadId }) {
  const nav = useNavigate();
  const [upload, setUpload] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [actionByCandidate, setActionByCandidate] = useState({});
  const [editsByCandidate, setEditsByCandidate] = useState({});
  const [topicHits, setTopicHits] = useState({});
  // K5 (2026-08-05) — diff filter + soft-delete toggle for re-ingests.
  const [diffFilter, setDiffFilter] = useState("all"); // all | added | changed | unchanged | removed
  const [removeRemoved, setRemoveRemoved] = useState(false);
  // UX-rework (2026-08-06) — categories for the per-row Category
  // dropdown. Fetched once on mount, silent-fail on error.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/atlas/categories")
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  // Undo-apply modal state (2026-08-06). See doUndo() below and the
  // <Trash2/> button in the review header. Modal opens on click,
  // requires typed-filename confirmation, then POSTs /undo.
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoConfirmText, setUndoConfirmText] = useState("");
  const [undoing, setUndoing] = useState(false);

  // Item 2 (2026-08-08) — Bulk approve/deny selection state. Only
  // pending candidates can be selected; non-pending rows have no
  // checkbox. Set-based storage so toggling is O(1).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [uR, cR] = await Promise.all([
        api.get(`/atlas/ingest/${uploadId}`),
        api.get(`/atlas/ingest/${uploadId}/candidates`),
      ]);
      setUpload(uR.data?.upload || null);
      const cands = cR.data?.candidates || [];
      setCandidates(cands);
      // Default action for each pending candidate = approve.
      const acc = {};
      const edits = {};
      for (const c of cands) {
        acc[c.id] = c.status === "pending"
          ? (c.is_unclassified
              ? "create_topic"
              : c.proposed_topic_slug ? "approve" : "route_to_queue")
          : c.status;
        edits[c.id] = {
          // K2-refinement — multi-target list (mirrors proposed_targets).
          // 2026-08-10 · Symptom 2 fix — prefer persisted server-side
          // edits (`c.edited_targets` / `c.edited_target_slug`) over
          // the LLM's original `proposed_*` seed so refreshing the
          // page doesn't revert the admin's target changes.
          edited_targets: Array.isArray(c.edited_targets) && c.edited_targets.length
            ? c.edited_targets.map((t) => ({ slug: t.slug || "", relationship: t.relationship || null }))
            : (Array.isArray(c.proposed_targets) && c.proposed_targets.length
                ? c.proposed_targets.map((t) => ({ slug: t.slug || "", relationship: t.relationship || null }))
                : ((c.edited_target_slug || c.proposed_topic_slug)
                    ? [{ slug: c.edited_target_slug || c.proposed_topic_slug, relationship: null }]
                    : [])),
          edited_target_slug: c.edited_target_slug || c.proposed_topic_slug || "",
          edited_display_type: c.edited_display_type || c.display_type || "narrative",
          edited_text: (c.edited_text != null) ? c.edited_text : (c.narrative_text || ""),
          edited_attribute_key: c.edited_attribute_key || c.attribute_key || "",
          edited_attribute_value: (c.edited_attribute_value != null)
            ? c.edited_attribute_value
            : (c.attribute_value || ""),
          edited_name: c.proposed_name || "",
          // Unclassified path defaults.
          create_category: (c.suggested_category || {}).name || "",
          create_category_parent_slug: (c.suggested_category || {}).parent_slug || "",
        };
      }
      setActionByCandidate(acc);
      setEditsByCandidate(edits);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setLoading(false); }
  }, [uploadId]);
  useEffect(() => { reload(); }, [reload]);

  const setAction = (cid, action) =>
    setActionByCandidate((prev) => ({ ...prev, [cid]: action }));
  // 2026-08-10 · Symptom 2 fix — persist target-slug / target-list
  // edits to the server so a page reload doesn't revert them. Only
  // the target-related fields need durable persistence for the
  // reported bug; text / attribute edits stay local until Apply
  // (unchanged existing behaviour). Fire-and-forget: any 4xx is
  // surfaced as a toast but does NOT block the local UI update.
  const persistTargetEdit = useCallback(async (cid, targetPatch) => {
    const body = {};
    if (targetPatch.edited_target_slug !== undefined) {
      body.edited_target_slug = targetPatch.edited_target_slug || "";
    }
    if (targetPatch.edited_targets !== undefined) {
      body.edited_targets = (targetPatch.edited_targets || [])
        .filter((t) => (t.slug || "").trim())
        .map((t) => ({ slug: t.slug, relationship: t.relationship || null }));
    }
    if (Object.keys(body).length === 0) return;
    try {
      await api.patch(`/atlas/ingest/candidates/${cid}`, body);
    } catch (e) {
      toast.error(`Couldn't save target change: ${formatApiErrorDetail(e)}`);
    }
  }, []);

  const setEdit = (cid, patch) => {
    setEditsByCandidate((prev) => ({ ...prev, [cid]: { ...prev[cid], ...patch } }));
    // 2026-08-10 · Symptom 2 fix — mirror target edits to the server
    // in the background so a page refresh keeps the picked folder.
    if (patch.edited_target_slug !== undefined
        || patch.edited_targets !== undefined) {
      const toSave = {};
      if (patch.edited_target_slug !== undefined) toSave.edited_target_slug = patch.edited_target_slug;
      if (patch.edited_targets !== undefined)     toSave.edited_targets     = patch.edited_targets;
      persistTargetEdit(cid, toSave);
    }
  };

  const bulkSet = (action) => {
    const acc = { ...actionByCandidate };
    for (const c of candidates) {
      if (c.status === "pending") acc[c.id] = action;
    }
    setActionByCandidate(acc);
  };

  const applyAll = async () => {
    const approvals = candidates
      .filter((c) => c.status === "pending")
      .map((c) => buildApprovalItem(c));
    if (approvals.length === 0) {
      toast.error("Nothing to apply");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(
        `/atlas/ingest/${uploadId}/apply`,
        { approvals, remove_removed: removeRemoved });
      toast.success(
        `Applied: ${data.facts_written} facts, ${data.routed_to_queue} routed, ${data.skipped} skipped`);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setApplying(false); }
  };

  // Bug-1 fix (2026-08-06) — per-row Save/Ignore MUST be atomic.
  // Prior behaviour called `applyAll` after setting the action for
  // ONE row, which then iterated every other pending row (defaults
  // to "approve") and silently wrote them all. Now we build a single-
  // item approvals list scoped to `cid` and POST only that.
  const applySingle = async (cid, action) => {
    const cand = candidates.find((c) => c.id === cid);
    if (!cand || cand.status !== "pending") return;
    // Update local action state so the UI reflects the choice.
    setActionByCandidate((prev) => ({ ...prev, [cid]: action }));
    // Build a single-candidate approvals payload. For `skip` we
    // don't need any edit fields — the backend just flips status.
    // For `approve` we still forward the row's edits so the fact
    // write picks up any inline changes the user made.
    let item;
    if (action === "skip") {
      item = { candidate_id: cid, action: "skip" };
    } else {
      item = buildApprovalItem({ ...cand }, action);
    }
    setApplying(true);
    try {
      const { data } = await api.post(
        `/atlas/ingest/${uploadId}/apply`,
        { approvals: [item], remove_removed: false });
      const savedN = data.facts_written || 0;
      const skipN = data.skipped || 0;
      if (action === "skip") {
        toast.success("Ignored this item");
      } else if (savedN > 0) {
        toast.success(`Saved · ${savedN} fact${savedN === 1 ? "" : "s"} written`);
      } else if (skipN > 0) {
        toast.success("Saved");
      } else {
        toast.success("Applied");
      }
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setApplying(false); }
  };

  // Item 2 — bulk selection helpers.
  const pendingCandidateIds = useMemo(
    () => candidates.filter((c) => c.status === "pending").map((c) => c.id),
    [candidates]);
  const allPendingSelected = pendingCandidateIds.length > 0
    && pendingCandidateIds.every((id) => selectedIds.has(id));
  const toggleSelect = (cid) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(cid)) n.delete(cid);
      else n.add(cid);
      return n;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((s) => {
      if (allPendingSelected) return new Set();
      return new Set(pendingCandidateIds);
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const applyBulk = async (action /* "approve" | "skip" */) => {
    const ids = Array.from(selectedIds).filter((id) => {
      const c = candidates.find((x) => x.id === id);
      return c && c.status === "pending";
    });
    if (ids.length === 0) {
      toast.error("Select at least one candidate");
      return;
    }
    const items = ids.map((cid) => {
      const c = candidates.find((x) => x.id === cid);
      if (action === "skip") return { candidate_id: cid, action: "skip" };
      return buildApprovalItem(c, "approve");
    });
    setBulkRunning(true);
    try {
      const { data } = await api.post(
        `/atlas/ingest/${uploadId}/apply`,
        { approvals: items, remove_removed: false });
      const savedN = data.facts_written || 0;
      const skipN = data.skipped || 0;
      if (action === "skip") {
        toast.success(`Denied ${ids.length} candidate${ids.length === 1 ? "" : "s"}`);
      } else {
        toast.success(
          `Applied · ${savedN} fact${savedN === 1 ? "" : "s"} written` +
          (skipN ? ` · ${skipN} skipped` : ""));
      }
      clearSelection();
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setBulkRunning(false); }
  };

  // Extracted so both applyAll and applySingle build items the same
  // way. `overrideAction` lets applySingle force a specific action
  // regardless of what's in actionByCandidate.
  function buildApprovalItem(c, overrideAction) {
    const action = overrideAction || actionByCandidate[c.id] || "skip";
    const edit = editsByCandidate[c.id] || {};
    const item = { candidate_id: c.id, action };
    if (action === "edit" || action === "approve" || action === "create_topic") {
      if (Array.isArray(edit.edited_targets) && edit.edited_targets.length) {
        item.edited_targets = edit.edited_targets
          .filter((t) => (t.slug || "").trim())
          .map((t) => ({ slug: t.slug, relationship: t.relationship || null }));
      }
      if (edit.edited_target_slug && edit.edited_target_slug !== c.proposed_topic_slug) {
        item.edited_target_slug = edit.edited_target_slug;
      }
      if (edit.edited_display_type && edit.edited_display_type !== c.display_type) {
        item.edited_display_type = edit.edited_display_type;
      }
      if (edit.edited_text && edit.edited_text !== c.narrative_text) {
        item.edited_text = edit.edited_text;
      }
      if (edit.edited_attribute_key && edit.edited_attribute_key !== c.attribute_key) {
        item.edited_attribute_key = edit.edited_attribute_key;
      }
      if (edit.edited_attribute_value && edit.edited_attribute_value !== c.attribute_value) {
        item.edited_attribute_value = edit.edited_attribute_value;
      }
      if (edit.edited_name && edit.edited_name !== c.proposed_name) {
        item.edited_name = edit.edited_name;
        item.new_topic_name = edit.edited_name;
      }
    }
    if (action === "create_topic" && c.is_unclassified && edit.create_category) {
      item.create_category = edit.create_category;
      if (edit.create_category_parent_slug) {
        item.create_category_parent_slug = edit.create_category_parent_slug;
      }
      if (edit.edited_name) item.new_topic_name = edit.edited_name;
    }
    return item;
  }

  // Undo-apply POST — reverses every fact + auto-created folder
  // written from this upload. Server-side idempotent; typed-filename
  // guard also enforced backend-side but we mirror it here so the
  // Confirm button stays disabled until the user types it exactly.
  const doUndo = async () => {
    if (!upload) return;
    const target = (upload.filename || "").trim();
    if (undoConfirmText.trim() !== target) {
      toast.error("Filename doesn't match — type it exactly.");
      return;
    }
    setUndoing(true);
    try {
      const { data } = await api.post(
        `/atlas/ingest/${uploadId}/undo`,
        { filename_confirmation: undoConfirmText.trim() });
      const dF = data.facts_deleted ?? 0;
      const dFol = data.folders_removed ?? 0;
      const kFol = data.folders_kept ?? 0;
      if (data.already_undone) {
        toast.success(
          `Already undone earlier · ${dF} fact${dF === 1 ? "" : "s"} were removed`
          + (dFol > 0 ? `, ${dFol} folder${dFol === 1 ? "" : "s"} removed` : "")
          + (kFol > 0 ? `, ${kFol} kept` : ""));
      } else {
        toast.success(
          `Undo complete · ${dF} fact${dF === 1 ? "" : "s"} removed · `
          + `${dFol} folder${dFol === 1 ? "" : "s"} removed`
          + (kFol > 0 ? ` · ${kFol} kept (had other content)` : ""));
      }
      setShowUndoModal(false); setUndoConfirmText("");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setUndoing(false); }
  };

  // Topic autocomplete per candidate.
  const searchTopics = async (cid, q) => {
    if (!q || q.length < 2) { setTopicHits((p) => ({ ...p, [cid]: [] })); return; }
    try {
      const { data } = await api.get(`/atlas/topics?q=${encodeURIComponent(q)}&limit=8`);
      setTopicHits((p) => ({ ...p, [cid]: data?.topics || [] }));
    } catch (_e) { setTopicHits((p) => ({ ...p, [cid]: [] })); }
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto p-6 font-hand text-tb-navy/60">Loading…</div>;
  }
  if (!upload) {
    return <div className="max-w-5xl mx-auto p-6 font-hand text-red-700">Upload not found.</div>;
  }

  const pendingCount = candidates.filter((c) => c.status === "pending").length;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="atlas-ingest-review-page">
      <button type="button" onClick={() => nav("/atlas/admin/ingest")}
        className="inline-flex items-center gap-1 text-tb-navy/60 hover:text-tb-navy text-xs font-hand mb-4"
        data-testid="atlas-ingest-review-back">
        <ArrowLeft className="w-3.5 h-3.5" /> All uploads
      </button>

      <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 mb-4"
           data-testid="atlas-ingest-review-header">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-tb-navy text-2xl"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              {upload.filename}
            </h1>
            <div className="mt-1 flex items-center gap-2 text-tb-navy/50 font-hand text-xs">
              <StatusPill status={upload.status} />
              <span>
                Uploaded {upload.uploaded_at ? new Date(upload.uploaded_at).toLocaleString() : "—"}
                {" · "}{upload.entity_count || 0} entities · {upload.fact_count || 0} facts
                {" · "}{Math.round((upload.size || 0) / 1024)} KB
              </span>
            </div>
          </div>
          {upload.storage_url && (
            <a href={upload.storage_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs hover:bg-tb-navy/5"
              data-testid="atlas-ingest-view-source">
              <ExternalLink className="w-3 h-3" /> View source document
            </a>
          )}
          {/* Undo-apply — red pill, only visible when this upload
              actually has something applied that could be reversed. */}
          {(upload.status === "applied"
              || candidates.some((c) => c.status === "approved")) && (
            <button type="button"
              onClick={() => { setUndoConfirmText(""); setShowUndoModal(true); }}
              data-testid="atlas-ingest-undo-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-red-300 bg-red-50 text-red-800 font-hand text-xs hover:bg-red-100">
              <Trash2 className="w-3 h-3" /> Undo apply
            </button>
          )}
          {upload.status === "undone" && (
            <span data-testid="atlas-ingest-undone-badge"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-tb-navy/5 text-tb-navy/60 font-hand text-xs border border-tb-navy/15">
              This upload was undone
              {upload.undone_at
                ? ` on ${new Date(upload.undone_at).toLocaleString()}`
                : ""}
            </span>
          )}
        </div>
        {upload.error && (
          <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-2 text-xs font-hand text-red-800 flex items-start gap-2"
               data-testid="atlas-ingest-error-banner">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{upload.error}</span>
          </div>
        )}
      </div>

      {/* K5 — diff strip on re-ingests. */}
      {upload.is_reingest && upload.diff_summary && (
        <div className="rounded-xl border border-tb-navy/15 bg-tb-navy/[0.02] p-3 mb-4"
             data-testid="atlas-ingest-diff-strip">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/60">
              Re-ingest diff:
            </span>
            {["added", "changed", "unchanged", "removed"].map((k) => (
              <button key={k} type="button"
                onClick={() => setDiffFilter(diffFilter === k ? "all" : k)}
                data-testid={`atlas-ingest-diff-filter-${k}`}
                className={
                  "inline-flex items-center gap-1 h-6 px-2 rounded-full font-hand text-[11px] border " +
                  (diffFilter === k
                    ? "bg-tb-navy text-tb-gold-bright border-tb-navy"
                    : (k === "added" ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                       k === "changed" ? "bg-amber-50 border-amber-300 text-amber-800" :
                       k === "removed" ? "bg-red-50 border-red-300 text-red-800" :
                       "bg-white border-tb-navy/20 text-tb-navy/70"))
                }>
                {upload.diff_summary[k] || 0} {k}
              </button>
            ))}
            {diffFilter !== "all" && (
              <button type="button" onClick={() => setDiffFilter("all")}
                data-testid="atlas-ingest-diff-filter-clear"
                className="text-[11px] font-hand text-tb-navy/50 hover:text-tb-navy underline underline-offset-2">
                clear filter
              </button>
            )}
            <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-hand text-tb-navy/70">
              <input type="checkbox"
                checked={removeRemoved}
                onChange={(e) => setRemoveRemoved(e.target.checked)}
                className="accent-red-600"
                data-testid="atlas-ingest-remove-removed-toggle" />
              Soft-delete removed facts on Apply
            </label>
          </div>
        </div>
      )}

      {/* UX-rework (2026-08-06) — plain-English intro banner. */}
      {pendingCount > 0 && (
        <div className="mb-3 rounded-lg border border-tb-navy/10 bg-tb-navy/[0.02] p-3 font-hand text-tb-navy/80 text-sm"
             data-testid="atlas-ingest-help-banner">
          TravelBio found <b>{pendingCount}</b> {pendingCount === 1 ? "item" : "items"} in this document.
          Click <b>Save this</b> on each you want to keep, or <b>Ignore this</b>. Click any name to rename.
        </div>
      )}

      {/* UX-rework — three-button top bar. Old chip-heavy bulk bar
          replaced with confidence-aware one-click actions. */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap"
             data-testid="atlas-ingest-bulk-actions">
          <button type="button"
            onClick={() => {
              const acc = { ...actionByCandidate };
              for (const c of candidates) {
                if (c.status !== "pending") continue;
                const conf = (c.target_confidence || 0);
                if (conf >= 0.8 && c.proposed_topic_slug) acc[c.id] = "approve";
              }
              setActionByCandidate(acc);
              // Fire apply immediately.
              setTimeout(() => applyAll(), 30);
            }}
            disabled={applying}
            data-testid="atlas-ingest-add-all-suggested"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-emerald-600 text-white font-hand text-sm disabled:opacity-50 hover:bg-emerald-700">
            <CheckIcon className="w-4 h-4" /> Save all suggested
          </button>
          <button type="button"
            onClick={() => {
              const acc = { ...actionByCandidate };
              for (const c of candidates) {
                if (c.status !== "pending") continue;
                const conf = (c.target_confidence || 0);
                if (conf < 0.6) acc[c.id] = "skip";
              }
              setActionByCandidate(acc);
              setTimeout(() => applyAll(), 30);
            }}
            disabled={applying}
            data-testid="atlas-ingest-skip-low-confidence"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy/5 hover:bg-tb-navy/10 border border-tb-navy/20 text-tb-navy font-hand text-sm disabled:opacity-50">
            <CloseIcon className="w-4 h-4" /> Ignore low-confidence
          </button>
          <button type="button"
            onClick={() => nav("/atlas/admin/ingest")}
            data-testid="atlas-ingest-cancel"
            className="ml-auto inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-tb-navy/60 hover:text-tb-navy font-hand text-sm">
            Cancel
          </button>
          {applying && (
            <span className="inline-flex items-center gap-1 text-xs font-hand text-tb-navy/60"
                  data-testid="atlas-ingest-applying-indicator">
              <Loader2 className="w-3 h-3 animate-spin" /> applying…
            </span>
          )}
        </div>
      )}

      {/* Item 2 — Bulk selection bar. Only rendered when 1+
          candidates are selected. Existing per-row Save/Ignore
          buttons stay intact. */}
      {selectedIds.size > 0 && (
        <div
          className="sticky top-0 z-10 flex items-center gap-2 rounded-xl border-2 border-tb-navy bg-white/95 backdrop-blur px-3 py-2 mb-2 shadow-sm"
          data-testid="atlas-ingest-bulk-bar"
        >
          <span className="text-sm font-hand text-tb-navy"
                data-testid="atlas-ingest-bulk-count">
            {selectedIds.size} selected
          </span>
          <button type="button"
            onClick={() => applyBulk("approve")}
            disabled={bulkRunning}
            data-testid="atlas-ingest-bulk-approve"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-tb-navy text-tb-gold-bright font-hand text-xs hover:brightness-110 disabled:opacity-50"
          >
            {bulkRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckIcon className="w-3 h-3" />}
            Approve selected
          </button>
          <button type="button"
            onClick={() => applyBulk("skip")}
            disabled={bulkRunning}
            data-testid="atlas-ingest-bulk-deny"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50"
          >
            {bulkRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <CloseIcon className="w-3 h-3" />}
            Deny selected
          </button>
          <button type="button"
            onClick={clearSelection}
            disabled={bulkRunning}
            data-testid="atlas-ingest-bulk-clear"
            className="ml-auto text-tb-navy/60 hover:text-tb-navy font-hand text-xs"
          >
            Clear
          </button>
        </div>
      )}

      {/* Select-all header (only shown when any candidates are pending) */}
      {pendingCandidateIds.length > 0 && (
        <label className="flex items-center gap-2 mb-2 text-xs font-hand text-tb-navy/60 select-none">
          <input
            type="checkbox"
            checked={allPendingSelected}
            onChange={toggleSelectAll}
            data-testid="atlas-ingest-select-all"
            className="w-4 h-4 rounded border-tb-navy/30 accent-tb-navy"
          />
          Select all pending ({pendingCandidateIds.length})
        </label>
      )}

      {/* Candidate rows */}
      <div className="space-y-2" data-testid="atlas-ingest-candidates">
        {candidates
          .filter((c) => diffFilter === "all" || (c.diff_status || "") === diffFilter)
          .map((c) => (
          <CandidateRowSimple key={c.id}
            c={c}
            siblings={candidates}
            categories={categories}
            action={actionByCandidate[c.id]}
            edit={editsByCandidate[c.id]}
            hits={topicHits[c.id] || []}
            selected={selectedIds.has(c.id)}
            onToggleSelect={() => toggleSelect(c.id)}
            onAction={(a) => setAction(c.id, a)}
            onEdit={(patch) => setEdit(c.id, patch)}
            onSearchTopics={(q) => searchTopics(c.id, q)}
            onSubmitSingle={() => applySingle(c.id, "approve")}
            onSkipSingle={() => applySingle(c.id, "skip")} />
        ))}
      </div>

      {/* Undo-apply confirmation modal (2026-08-06). Requires the
          admin to type the exact filename to enable the red Confirm
          button — mirrors the destructive-action UX used elsewhere
          in Atlas admin. */}
      {showUndoModal && (
        <div className="fixed inset-0 z-50 bg-tb-navy/40 flex items-center justify-center p-4"
             data-testid="atlas-ingest-undo-modal"
             onClick={(e) => { if (e.target === e.currentTarget) setShowUndoModal(false); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-lg">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-tb-navy text-lg leading-tight"
                    style={{ fontFamily: '"Playfair Display", serif' }}>
                  Undo this upload?
                </h3>
                <p className="mt-1 font-hand text-tb-navy/70 text-sm">
                  This will remove all facts, folders and attributes
                  created from <b>{upload.filename}</b>
                  {typeof upload.fact_count === "number"
                    ? ` (${upload.fact_count} fact${upload.fact_count === 1 ? "" : "s"})`
                    : ""}. Folders that hold content from other
                  uploads will be kept.
                </p>
              </div>
            </div>
            <label className="block mt-3 font-hand text-tb-navy/70 text-xs">
              Type the filename to confirm:
              <div className="mt-1 font-mono text-[11px] text-tb-navy/50 select-all">{upload.filename}</div>
              <input type="text"
                value={undoConfirmText}
                onChange={(e) => setUndoConfirmText(e.target.value)}
                autoFocus
                disabled={undoing}
                data-testid="atlas-ingest-undo-confirm-input"
                className="mt-1 w-full h-9 px-3 rounded-lg border border-tb-navy/25 bg-white font-mono text-sm text-tb-navy focus:outline-none focus:border-red-500"
                placeholder="filename.ext" />
            </label>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button"
                onClick={() => { setShowUndoModal(false); setUndoConfirmText(""); }}
                disabled={undoing}
                data-testid="atlas-ingest-undo-cancel"
                className="h-9 px-4 rounded-full text-tb-navy/70 hover:text-tb-navy font-hand text-sm">
                Cancel
              </button>
              <button type="button"
                onClick={doUndo}
                disabled={undoing || undoConfirmText.trim() !== (upload.filename || "").trim()}
                data-testid="atlas-ingest-undo-confirm"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-red-600 text-white font-hand text-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                {undoing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {undoing ? "Undoing…" : "Yes, undo this upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// UX-rework (2026-08-06) — dead-simple candidate row.
//
// Design principles:
//  • Only 5 visible elements: icon + editable name + plain-English
//    subtitle + folder picker + Add / Skip buttons.
//  • Everything else (multi-target, category, route-to-queue,
//    confidence badge) lives behind an Advanced chevron, collapsed
//    by default.
//  • No pill soup, no `#slug` chips, no dropdown lists. Ships as a
//    card, reads like a to-do item.
// ─────────────────────────────────────────────────────────────
const _RELATIONSHIP_PHRASE = {
  homeport:      (root) => `Port that ${root || "this ship"} homeports out of`,
  port_of_call:  (root) => `Port that ${root || "this ship"} visits`,
  region_sailed: (root) => `Region ${root || "this ship"} sails`,
  cabin_class:   (root) => `Cabin category on ${root || "this ship"}`,
  room_type:     (root) => `Room type at ${root || "this property"}`,
  operated_by:   (root) => `Operator of ${root || "this"}`,
  cruise_line:   (root) => `Cruise line that runs ${root || "this ship"}`,
  sister_ship:   (root) => `Sister ship of ${root || "this ship"}`,
  flag_state:    (root) => `Flag ${root || "this ship"} sails under`,
  attraction:    (root) => `Attraction near ${root || "this place"}`,
};

function _buildSubtitle(c) {
  if (c.role === "root")
    return `Top-level ${c.proposed_entity_type || "topic"} from this document`;
  if (c.role === "linked" && c.relationship_to_root) {
    const fn = _RELATIONSHIP_PHRASE[c.relationship_to_root];
    if (fn) return fn(c.root_entity_ref);
    return `${c.relationship_to_root.replace(/_/g, " ")} — ${c.root_entity_ref || ""}`.trim();
  }
  if (c.role === "unclassified")
    return c.unclassified_reason
      ? `Needs your review — ${c.unclassified_reason}`
      : "Needs your review";
  if (c.kind === "fact") {
    if (c.display_type === "advisor_tip") return "Advisor tip";
    if (c.display_type === "attribute" && c.attribute_key)
      return `Attribute · ${c.attribute_key.replace(/_/g, " ")}`;
    return "Narrative fact";
  }
  return c.proposed_entity_type || "Topic from this document";
}

function _iconForEntityType(etype) {
  const map = {
    cruise_line:  "🚢",   cruise_ship:  "⛴️",   ship_class:   "⚓",
    port:         "⚓",   resort:       "🏝️",   hotel:        "🏨",
    destination:  "📍",   region:       "🗺️",   country:      "🏳️",
    city:         "🏙️",   cabin_class:  "🛏️",   room_type:    "🛏️",
    attraction:   "🎡",   restaurant:   "🍽️",   airline:      "✈️",
    category:     "📁",   other:        "•",
  };
  return map[etype] || "•";
}

function CandidateRowSimple({ c, edit, hits, categories = [], siblings = [],
                              selected = false, onToggleSelect, action,
                              onEdit, onSearchTopics,
                              onSubmitSingle, onSkipSingle }) {
  const isPending = c.status === "pending";
  const [renaming, setRenaming] = React.useState(false);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pickerDraft, setPickerDraft] = React.useState("");

  // 2026-08-06 — Attribute-first ingestion. Only two Type values are
  // allowed now: `attribute` and `narrative`. Folder / create-topic
  // mode was removed per user directive; attributes are the default.
  const inferredType = (c.kind === "fact" && c.display_type === "narrative")
    ? "narrative"
    : (c.kind === "fact" && c.display_type === "advisor_tip")
      ? "narrative" // advisor_tip is a UI toggle on top of narrative
      : "attribute";
  const rowType = edit?.row_type || inferredType;
  const setRowType = (t) => onEdit({ row_type: t });
  // Advisor-Tip is a boolean toggle available on narrative rows only.
  // It flips the persisted display_type between narrative and
  // advisor_tip without needing a separate Type value.
  const advisorTipOn = (edit?.edited_display_type
      ? edit.edited_display_type
      : (c.display_type || "")) === "advisor_tip";
  const setAdvisorTip = (on) => onEdit({
    edited_display_type: on ? "advisor_tip" : "narrative",
  });

  // "What goes inside" — only meaningful when Type=Folder. Default
  // is "as sub-folders" for linked entities, "as list" when the
  // extract looks like a flat similar-shape list. Users can flip.
  const inferredInside =
    c.role === "linked" ? "sub_folders"
    : (c.raw_extract && Array.isArray(c.raw_extract.list_items)) ? "list_attribute"
    : "sub_folders";
  const insideMode = edit?.folder_inside_mode || inferredInside;
  const setInsideMode = (m) => onEdit({ folder_inside_mode: m });

  // Category selection — free-form or existing.
  const catId = edit?.category_id || "";
  const setCatId = (v) => onEdit({ category_id: v });

  const primarySlug = edit?.edited_target_slug || c.proposed_topic_slug || "";
  // Was the target manually overridden via the folder picker? When
  // yes we prefer the picked hit's own name (`edited_target_display`
  // stored at pick-time) for the breadcrumb button and the preview's
  // "under X" phrase — otherwise the UI would keep showing the stale
  // original placement_path from the server.
  const targetPicked = !!edit?.edited_target_slug
    && edit.edited_target_slug !== (c.proposed_topic_slug || "");
  const pickedDisplay = targetPicked
    ? (edit?.edited_target_display || edit.edited_target_slug)
    : null;
  const primaryBreadcrumb = pickedDisplay
    ? pickedDisplay
    : (Array.isArray(c.placement_path) && c.placement_path.length
        ? c.placement_path.map((p) => p.name).join(" › ")
        : (primarySlug || "— choose a folder —"));
  // Mark the placement as (new) when the terminal folder is one we'll
  // create at Save time (no slug resolves yet). Once the user picks
  // an existing folder via the picker, this becomes false.
  const placementNeedsCreate = targetPicked
    ? false
    : (c.is_unclassified || !primarySlug
       || (Array.isArray(c.placement_path)
           && !c.placement_path[c.placement_path.length - 1]?.slug));

  const name = edit?.edited_name || c.proposed_name || "";
  const icon = _iconForEntityType(c.proposed_entity_type);
  const subtitle = _buildSubtitle(c);

  const targets = Array.isArray(edit?.edited_targets) ? edit.edited_targets : [];
  const addExtraTarget = (slug) => {
    if (!slug || targets.some((t) => t.slug === slug)) return;
    onEdit({ edited_targets: [...targets, { slug, relationship: null }] });
  };
  const removeExtraTarget = (slug) =>
    onEdit({ edited_targets: targets.filter((t) => t.slug !== slug) });

  const pickerSearch = (q) => {
    setPickerDraft(q);
    onSearchTopics(q);
  };

  // ── Live preview sentence (updates reactively). ──
  // parentName drives the preview's "under X" phrase for folder-
  // creation rows. When the user has manually picked a destination
  // via the folder picker, we use that pick as the "under" target
  // so the preview stays in sync with the picker button.
  const parentName = pickedDisplay
    ? pickedDisplay
    : (c.placement_path || [])
        .slice(0, -1).map((p) => p.name).join(" › ")
        || c.root_entity_ref || "the root";
  // Bug-2 fix (2026-08-06) — full breadcrumb for the resolves-to-
  // existing preview so advisors can see exactly where the facts
  // will attach (e.g. `Fleet › Odyssey of the Seas`).
  const fullBreadcrumb = (c.placement_path || [])
    .map((p) => p.name).join(" › ");
  // Truthy when the row's primary target is an already-existing
  // topic (verified server-side via `_resolve_topic_slug`). Used to
  // rewrite the preview so we never claim "Creates folder…" for a
  // topic that's already in the tree.
  const attachesToExisting = !!c.resolves_to_existing && !!primarySlug && !c.is_unclassified;

  // Direct parent NAME (for preview text). For a LINKED candidate
  // we prefer the semantic root (root_entity_ref) — the intended
  // attribute target — over the tree parent that might be
  // unrelated (e.g. Caribbean lives under Destinations in the tree
  // but the user's intent is to file it against Odyssey).
  const _pp = c.placement_path || [];
  const directParent = _pp.length >= 2 ? _pp[_pp.length - 2] : null;
  const directParentName = (c.role === "linked" && c.root_entity_ref)
    ? c.root_entity_ref
    : (directParent?.name || c.root_entity_ref || "the parent");

  // 2026-08-06 — simplified anchor UX. When a candidate resolves to
  // an EXISTING topic AND is the entity anchor for the document
  // (role='root') or an existing linked entity, the full row of
  // Type/Category/Folder-name/What-goes-inside/Goes-here inputs is
  // confusing (nothing to create). We collapse the row to a plain
  // info banner + a summary of what will attach + a "Confirm anchor"
  // button that's a no-op idempotent approve.
  const isExistingAnchor = attachesToExisting && c.role === "root";
  const isExistingLinked = attachesToExisting && c.role === "linked";
  const isSimplifiedExisting = isExistingAnchor || isExistingLinked;

  // Downstream candidates that will attach to this anchor. Matched
  // by `root_entity_ref` for linked/fact_on_root children.
  const attachingSiblings = React.useMemo(() => {
    if (!isSimplifiedExisting) return [];
    const anchorName = (c.proposed_name || "").trim();
    if (!anchorName) return [];
    return (siblings || []).filter((s) =>
      s && s.id !== c.id
      && s.status === "pending"
      && (s.root_entity_ref || "").trim() === anchorName);
  }, [siblings, c, isSimplifiedExisting]);

  // 2026-08-06 P0 fix — humanised default for the attribute name.
  // Turns `region_sailed` → `Region Sailed`.  Used both to seed the
  // inline attribute inputs and to render the live preview.
  const humanizeRel = (raw) => (raw || "")
    .replace(/_/g, " ")
    .replace(/\b(\w)/g, (m) => m.toUpperCase());
  const defaultAttrKey = (
    (c.attribute_key && c.attribute_key.trim())
    || ((c.raw_extract || {}).attribute_key || "").trim()
    || (c.relationship_to_root ? humanizeRel(c.relationship_to_root) : "")
    || "attribute");
  const defaultAttrValue = (
    (c.attribute_value && c.attribute_value.trim())
    || ((c.raw_extract || {}).attribute_value || "").trim()
    || (c.proposed_name || "").trim());
  // Parent-topic slug used when the user flips Type from Folder →
  // Attribute on a linked entity. Attributes attach to the ROOT
  // entity's folder (semantic parent) — for the Caribbean example,
  // that's Odyssey of the Seas, NOT Caribbean's own tree parent
  // (Destinations).  Falls back to the second-to-last placement
  // node when no root sibling can be located.
  const parentSlugForAttribute = (() => {
    if (c.role === "linked" && Array.isArray(siblings) && c.root_entity_ref) {
      const rootCand = siblings.find((s) =>
        s && s.role === "root"
        && (s.proposed_name || "").trim() === (c.root_entity_ref || "").trim());
      if (rootCand?.proposed_topic_slug) return rootCand.proposed_topic_slug;
    }
    const pp = c.placement_path || [];
    if (pp.length >= 2) {
      const p = pp[pp.length - 2];
      if (p && p.slug) return p.slug;
    }
    return null;
  })();

  // When the user flips the Type dropdown to `attribute` on an
  // existing linked/root row, prefill the attribute key/value and
  // retarget "Goes here" to the parent (attributes attach to the
  // parent folder, not to the linked entity itself). Only fires
  // when the user's edits are still empty — never clobbers manual
  // input.
  // Keep `edited_display_type` in lockstep with the Type dropdown +
  // Advisor Tip toggle. Ensures apply-time writes the user's intent.
  React.useEffect(() => {
    const target = rowType === "attribute"
      ? "attribute"
      : (advisorTipOn ? "advisor_tip" : "narrative");
    if ((edit?.edited_display_type || c.display_type || "") !== target) {
      onEdit({ edited_display_type: target });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowType, advisorTipOn]);

  React.useEffect(() => {
    if (rowType !== "attribute") return;
    if (!isSimplifiedExisting) return;
    const patch = {};
    if (!edit?.edited_attribute_key)   patch.edited_attribute_key   = defaultAttrKey;
    if (!edit?.edited_attribute_value) patch.edited_attribute_value = defaultAttrValue;
    // Retarget to the parent slug ONLY when the user hasn't already
    // manually overridden the target. The row-mount initialiser
    // seeds edit.edited_target_slug with c.proposed_topic_slug, so
    // we treat that specific value as "still default".
    const targetIsDefault =
      !edit?.edited_target_slug
      || edit?.edited_target_slug === (c.proposed_topic_slug || "");
    if (parentSlugForAttribute
        && targetIsDefault
        && (c.proposed_topic_slug || "") !== parentSlugForAttribute) {
      patch.edited_target_slug = parentSlugForAttribute;
    }
    if (Object.keys(patch).length) onEdit(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowType]);

  // Banner only makes sense while the user is still on the DEFAULT
  // "attach to existing" interpretation. Once they flip Type or
  // pick a different destination, the banner is stale — hide it.
  // Note: `edit.edited_target_slug` is PRE-INITIALISED at row mount
  // to c.proposed_topic_slug, so we only treat it as an "override"
  // when it differs from the proposed value.
  const targetOverridden =
    !!edit?.edited_target_slug
    && edit.edited_target_slug !== (c.proposed_topic_slug || "");
  const bannerActive = isSimplifiedExisting
    && rowType === inferredType
    && !targetOverridden;

  const preview = React.useMemo(() => {
    const nm = name || "this item";
    if (rowType === "attribute") {
      const val = edit?.edited_attribute_value ?? defaultAttrValue ?? nm;
      const key = edit?.edited_attribute_key   ?? defaultAttrKey;
      if (targetPicked) {
        return `Adds attribute ${key}: ${val} to ${pickedDisplay}`;
      }
      if (attachesToExisting && c.role === "linked") {
        return `Adds attribute ${key}: ${val} to existing folder ${directParentName}`;
      }
      if (attachesToExisting) {
        return `Adds attribute ${key}: ${val} to existing folder ${nm} (${fullBreadcrumb})`;
      }
      return `Adds attribute ${key}: ${val} to ${parentName}`;
    }
    // rowType === "narrative" (advisor_tip is a UI toggle on top of it)
    const body = (edit?.edited_text || c.narrative_text || c.attribute_value || nm).trim();
    const target = targetPicked
      ? pickedDisplay
      : (attachesToExisting ? nm : (parentName || nm));
    const label = advisorTipOn ? "Advisor tip" : "Narrative";
    const preview_body = body.length > 100 ? body.slice(0, 100) + "…" : body;
    return `Adds ${label}: "${preview_body}" to ${target}`;
  }, [rowType, name, parentName, fullBreadcrumb, directParentName,
       attachesToExisting, targetPicked, pickedDisplay, advisorTipOn,
       defaultAttrKey, defaultAttrValue,
       edit?.edited_attribute_key, edit?.edited_attribute_value,
       edit?.edited_text, c]);

  return (
    <div
      data-testid={`atlas-ingest-candidate-${c.id}`}
      data-kind={c.kind}
      data-role={c.role || ""}
      data-status={c.status}
      data-row-type={rowType}
      className={
        "rounded-2xl border p-3 md:p-4 transition-shadow " +
        (!isPending
          ? "border-tb-navy/10 bg-tb-navy/[0.02] opacity-60"
          : "border-tb-navy/15 bg-white hover:shadow-sm")
      }
    >
      <div className="flex items-start gap-3">
        {/* Item 2 — Bulk select checkbox. Only rendered for pending
            rows; non-pending rows have no checkbox because they've
            already been actioned. */}
        {isPending && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            data-testid={`atlas-ingest-select-${c.id}`}
            className="mt-1.5 w-4 h-4 rounded border-tb-navy/30 accent-tb-navy flex-shrink-0"
            aria-label="Select for bulk action"
          />
        )}
        {/* Icon */}
        <div className="text-2xl mt-0.5 select-none" aria-hidden
             data-testid={`atlas-ingest-icon-${c.id}`}>
          {icon}
        </div>

        {/* Name + subtitle + controls */}
        <div className="flex-1 min-w-0">
          {renaming && isPending ? (
            <input autoFocus
              value={name}
              onChange={(e) => onEdit({ edited_name: e.target.value })}
              onBlur={() => setRenaming(false)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setRenaming(false); }}
              data-testid={`atlas-ingest-name-input-${c.id}`}
              className="w-full text-lg text-tb-navy font-semibold bg-transparent border-b border-tb-navy/30 focus:outline-none focus:border-tb-navy"
              style={{ fontFamily: '"Playfair Display", serif' }} />
          ) : (
            <button type="button"
              onClick={() => isPending && setRenaming(true)}
              disabled={!isPending}
              data-testid={`atlas-ingest-name-${c.id}`}
              className="text-lg text-tb-navy font-semibold text-left hover:underline underline-offset-2 decoration-tb-navy/30 truncate max-w-full"
              style={{ fontFamily: '"Playfair Display", serif' }}
              title={isPending ? "Click to rename" : ""}>
              {name || "(unnamed)"}
            </button>
          )}
          <div className="mt-0.5 font-hand text-tb-navy/60 text-sm"
               data-testid={`atlas-ingest-subtitle-${c.id}`}>
            {subtitle}
          </div>

          {/* 2026-08-06 — simplified banner for existing anchors /
              linked entities. Explains that no new folder will be
              created and summarises what will attach if saved.
              Hides itself once the user overrides Type or picks a
              different "Goes here" — the banner would be stale. */}
          {isPending && bannerActive && (
            <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/70 p-3"
                 data-testid={`atlas-ingest-existing-banner-${c.id}`}>
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-hand text-sky-900 text-sm">
                    <b>{name}</b> already exists in your tree at{" "}
                    <span className="font-mono text-[12px] bg-white/70 px-1.5 py-0.5 rounded border border-sky-200">
                      {fullBreadcrumb || primaryBreadcrumb}
                    </span>.
                    {isExistingAnchor
                      ? " The facts and sub-items from this document (rows below) will attach to it."
                      : " The facts targeting this folder from this document will attach to it."}
                    {" "}Saving this row is optional — it's just the anchor.
                  </div>
                  {isExistingAnchor && attachingSiblings.length > 0 && (
                    <div className="mt-2 font-hand text-sky-900/80 text-xs"
                         data-testid={`atlas-ingest-anchor-summary-${c.id}`}>
                      <b>{attachingSiblings.length}</b> linked item
                      {attachingSiblings.length === 1 ? "" : "s"} will attach to this folder if saved:
                      <ul className="mt-1 space-y-0.5 pl-4 list-disc">
                        {attachingSiblings.slice(0, 8).map((s) => (
                          <li key={s.id} className="truncate">
                            {s.proposed_name || s.attribute_key || "(unnamed)"}
                          </li>
                        ))}
                        {attachingSiblings.length > 8 && (
                          <li className="text-sky-900/60">
                            …and {attachingSiblings.length - 8} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  {isExistingAnchor && attachingSiblings.length === 0 && (
                    <div className="mt-1 font-hand text-sky-900/60 text-xs"
                         data-testid={`atlas-ingest-anchor-summary-${c.id}`}>
                      No linked items in this document target this folder.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Type + Category selectors — always visible + interactive
              on pending rows. User's Type choice ALWAYS wins, even
              when the entity resolves to an existing topic. */}
          {isPending && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
                  Type
                </span>
                <select
                  value={rowType}
                  onChange={(e) => setRowType(e.target.value)}
                  data-testid={`atlas-ingest-type-${c.id}`}
                  className="mt-1 w-full h-9 px-2 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
                  <option value="attribute">Attribute (label + value)</option>
                  <option value="narrative">Narrative (free-text insight)</option>
                </select>
              </label>
              {rowType === "narrative" && (
                <label className="flex items-center gap-2 mt-6 font-hand text-tb-navy text-sm cursor-pointer"
                       data-testid={`atlas-ingest-advisor-tip-toggle-${c.id}`}>
                  <input type="checkbox"
                    checked={advisorTipOn}
                    onChange={(e) => setAdvisorTip(e.target.checked)}
                    className="h-4 w-4 accent-amber-500" />
                  <span>Highlight as <b>Advisor Tip</b> 💡</span>
                </label>
              )}
              {rowType !== "narrative" && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
                  Group into category
                </span>
                <select
                  value={catId}
                  onChange={(e) => {
                    if (e.target.value === "__new__") {
                      const v = window.prompt("Name your new category:");
                      if (v && v.trim()) onEdit({ category_id: "__new__", create_category: v.trim() });
                      else e.target.value = catId;
                    } else {
                      setCatId(e.target.value);
                      onEdit({ create_category: "" });
                    }
                  }}
                  data-testid={`atlas-ingest-category-${c.id}`}
                  className="mt-1 w-full h-9 px-2 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
                  <option value="">— none —</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                  <option value="__new__">＋ Create new…</option>
                </select>
              </label>
              )}
            </div>
          )}

          {/* Folder-name / What-goes-inside blocks REMOVED — 2026-08-06
              attribute-first pass. Folder mode no longer exists in
              the ingestion UI. Existing rows default to Attribute or
              Narrative. */}

          {/* Inline attribute inputs — appear whenever Type=Attribute.
              Previously only lived in the "More options" collapsible,
              which meant users couldn't edit the attribute name/value
              from the primary UI. */}
          {isPending && rowType === "attribute" && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
                  Attribute name
                </span>
                <input
                  value={edit?.edited_attribute_key ?? defaultAttrKey}
                  onChange={(e) => onEdit({ edited_attribute_key: e.target.value })}
                  data-testid={`atlas-ingest-attr-key-inline-${c.id}`}
                  placeholder="e.g. Homeport, Regions Sailed"
                  className="mt-1 w-full h-9 px-2 rounded-lg border border-tb-navy/25 font-hand text-sm text-tb-navy" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
                  Attribute value
                </span>
                <input
                  value={edit?.edited_attribute_value ?? defaultAttrValue}
                  onChange={(e) => onEdit({ edited_attribute_value: e.target.value })}
                  data-testid={`atlas-ingest-attr-value-inline-${c.id}`}
                  placeholder="e.g. Caribbean"
                  className="mt-1 w-full h-9 px-2 rounded-lg border border-tb-navy/25 font-hand text-sm text-tb-navy" />
              </label>
            </div>
          )}


          {/* Inline narrative textarea for Type=Narrative rows. Lets
              the user tweak the free-text insight before saving. */}
          {isPending && rowType === "narrative" && (
            <div className="mt-2">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
                  {advisorTipOn ? "Advisor tip" : "Narrative text"}
                </span>
                <textarea
                  value={edit?.edited_text ?? (c.narrative_text || c.attribute_value || "")}
                  onChange={(e) => onEdit({ edited_text: e.target.value })}
                  rows={2}
                  data-testid={`atlas-ingest-narrative-text-${c.id}`}
                  placeholder="Write the insight or tip advisors should see…"
                  className="mt-1 w-full px-2 py-1 rounded-lg border border-tb-navy/25 font-hand text-sm text-tb-navy" />
              </label>
            </div>
          )}

          {/* Goes here — plain-English placement. Always visible + 
              interactive on pending rows. User must be able to
              retarget destination even when the entity resolves to 
              an existing topic. */}
          <div className="mt-3 relative">
            <label className="block text-[10px] uppercase tracking-widest font-hand text-tb-navy/40 mb-1">
              Goes here
            </label>
            <button type="button"
              onClick={() => isPending && setPickerOpen((v) => !v)}
              disabled={!isPending}
              data-testid={`atlas-ingest-folder-picker-${c.id}`}
              className="w-full text-left px-3 h-9 rounded-lg border border-tb-navy/25 bg-white hover:border-tb-navy/50 font-hand text-sm text-tb-navy truncate flex items-center gap-2">
              <span className="truncate flex-1">📁&nbsp; {primaryBreadcrumb}</span>
              {placementNeedsCreate && (
                <span className="text-[10px] uppercase tracking-widest font-hand text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5">
                  new
                </span>
              )}
              <span className="text-tb-navy/40 text-xs">✎</span>
            </button>
            {pickerOpen && isPending && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-lg border border-tb-navy/15 bg-white shadow-xl max-h-64 overflow-y-auto"
                   data-testid={`atlas-ingest-folder-picker-open-${c.id}`}>
                <input autoFocus
                  value={pickerDraft}
                  onChange={(e) => pickerSearch(e.target.value)}
                  placeholder="Change or create new folder…"
                  data-testid={`atlas-ingest-folder-search-${c.id}`}
                  className="w-full h-9 px-3 border-b border-tb-navy/10 font-hand text-sm text-tb-navy focus:outline-none" />
                {hits.length === 0 && pickerDraft.length >= 2 && (
                  <div className="px-3 py-2 font-hand text-xs text-tb-navy/50">
                    No folders match — the current name will be created as a new folder.
                  </div>
                )}
                {hits.map((h) => (
                  <button key={h.slug} type="button"
                    onClick={() => {
                      // Store both the slug AND a display name so the
                      // Goes-here breadcrumb button + preview text
                      // reflect the pick immediately without waiting
                      // for another topic-lookup round-trip.
                      onEdit({
                        edited_target_slug:    h.slug,
                        edited_target_display: h.name,
                      });
                      setPickerOpen(false); setPickerDraft("");
                    }}
                    data-testid={`atlas-ingest-folder-pick-${c.id}-${h.slug}`}
                    className="w-full text-left px-3 py-2 font-hand text-sm hover:bg-tb-navy/5">
                    <span className="text-tb-navy">{h.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Live preview */}
          {isPending && (
            <div className="mt-3 rounded-lg bg-tb-navy/[0.03] border border-tb-navy/10 px-3 py-2"
                 data-testid={`atlas-ingest-preview-${c.id}`}>
              <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
                What happens when you click Save
              </span>
              <div className="mt-0.5 font-hand text-tb-navy text-sm">
                {preview}
              </div>
            </div>
          )}
        </div>

        {/* Save / Ignore buttons */}
        {isPending && (
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button type="button"
              onClick={onSubmitSingle}
              data-testid={`atlas-ingest-add-${c.id}`}
              title={isExistingAnchor
                ? "This folder already exists — clicking here just confirms it as the anchor for the facts below."
                : ""}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald-600 text-white font-hand text-sm hover:bg-emerald-700">
              <CheckIcon className="w-4 h-4" />
              {isExistingAnchor ? "Confirm anchor" : "Save this"}
            </button>
            <button type="button"
              onClick={onSkipSingle}
              data-testid={`atlas-ingest-skip-${c.id}`}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tb-navy/5 border border-tb-navy/20 text-tb-navy/70 font-hand text-sm hover:bg-tb-navy/10">
              <CloseIcon className="w-4 h-4" /> Ignore this
            </button>
          </div>
        )}
        {!isPending && (
          <span className="inline-flex items-center h-7 px-3 rounded-full bg-emerald-100 text-emerald-800 font-hand text-[11px] uppercase tracking-widest"
                data-testid={`atlas-ingest-cstatus-${c.id}`}>
            {c.status === "approved" ? "saved" : c.status === "skipped" ? "ignored" : c.status}
          </span>
        )}
      </div>

      {/* More options chevron */}
      {isPending && (
        <div className="mt-3 pt-2 border-t border-tb-navy/10">
          <button type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid={`atlas-ingest-advanced-toggle-${c.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-hand text-tb-navy/50 hover:text-tb-navy">
            {advancedOpen ? "▾" : "▸"} More options
          </button>
          {advancedOpen && (
            <div className="mt-2 space-y-3 rounded-lg border border-dashed border-tb-navy/15 bg-tb-navy/[0.015] p-3"
                 data-testid={`atlas-ingest-advanced-${c.id}`}>
              {/* Also save to another folder (multi-target) */}
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-hand text-tb-navy/40 mb-1">
                  Also save to another folder
                </label>
                <div className="flex items-center gap-1.5 flex-wrap"
                     data-testid={`atlas-ingest-targets-${c.id}`}>
                  {targets
                    .filter((t) => t.slug && t.slug !== primarySlug)
                    .map((t) => (
                    <span key={t.slug}
                      data-testid={`atlas-ingest-target-chip-${c.id}-${t.slug}`}
                      className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-full bg-tb-navy/10 border border-tb-navy/15 text-tb-navy font-hand text-[11px]">
                      <span>{t.slug}</span>
                      <button type="button"
                        onClick={() => removeExtraTarget(t.slug)}
                        data-testid={`atlas-ingest-target-remove-${c.id}-${t.slug}`}
                        className="w-4 h-4 rounded-full hover:bg-red-100 text-red-700">×</button>
                    </span>
                  ))}
                  <ExtraTargetAdder onAdd={addExtraTarget} candidateId={c.id} />
                </div>
              </div>

              {/* Not sure — send to review later */}
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button"
                  onClick={onSkipSingle}
                  data-testid={`atlas-ingest-route-queue-${c.id}`}
                  className="h-7 px-3 rounded-full bg-tb-navy/5 hover:bg-tb-navy/10 text-tb-navy font-hand text-[11px]">
                  Not sure — send to review later
                </button>
              </div>

              {/* Signals row */}
              <div className="flex items-center gap-3 flex-wrap font-hand text-[11px] text-tb-navy/50">
                {c.target_confidence != null && (
                  <span data-testid={`atlas-ingest-conf-${c.id}`}>
                    confidence {Math.round(c.target_confidence * 100)}%
                  </span>
                )}
                {c.has_conflict && (
                  <span className="text-amber-800" data-testid={`atlas-ingest-conflict-${c.id}`}>
                    ⚠ conflict
                  </span>
                )}
                {c.diff_status && (
                  <span data-testid={`atlas-ingest-diff-pill-${c.id}`}>
                    diff: {c.diff_status}
                  </span>
                )}
                <span>order #{c.order}</span>
              </div>

              {/* Fact editors — only when this is a fact candidate */}
              {rowType === "attribute" && (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-hand text-tb-navy/40 mb-1">
                    Change name to
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      value={edit?.edited_attribute_key || ""}
                      onChange={(e) => onEdit({ edited_attribute_key: e.target.value })}
                      placeholder="attribute name (e.g. homeport)"
                      data-testid={`atlas-ingest-attr-key-${c.id}`}
                      className="flex-1 min-w-[120px] h-7 px-2 rounded border border-tb-navy/25 font-hand text-[11px] text-tb-navy" />
                    <span className="text-tb-navy/40 font-hand text-xs">=</span>
                    <input
                      value={edit?.edited_attribute_value || ""}
                      onChange={(e) => onEdit({ edited_attribute_value: e.target.value })}
                      placeholder="value"
                      data-testid={`atlas-ingest-attr-value-${c.id}`}
                      className="flex-1 min-w-[140px] h-7 px-2 rounded border border-tb-navy/25 font-hand text-[11px] text-tb-navy" />
                  </div>
                </div>
              )}
              {c.kind === "fact" && rowType !== "attribute" && (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest font-hand text-tb-navy/40 mb-1">
                    Change text to
                  </label>
                  <textarea
                    value={edit?.edited_text || ""}
                    onChange={(e) => onEdit({ edited_text: e.target.value })}
                    rows={2}
                    data-testid={`atlas-ingest-text-${c.id}`}
                    className="w-full px-2 py-1.5 rounded border border-tb-navy/25 font-hand text-[12px] text-tb-navy resize-none" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExtraTargetAdder({ onAdd, candidateId }) {
  const [q, setQ] = React.useState("");
  const [hits, setHits] = React.useState([]);
  const search = async (v) => {
    setQ(v);
    if (!v || v.length < 2) { setHits([]); return; }
    try {
      const { data } = await api.get(`/atlas/topics?q=${encodeURIComponent(v)}&limit=6`);
      setHits(data?.topics || []);
    } catch { setHits([]); }
  };
  return (
    <div className="relative inline-block">
      <input
        value={q}
        onChange={(e) => search(e.target.value)}
        placeholder="+ another folder"
        data-testid={`atlas-ingest-target-add-input-${candidateId}`}
        className="h-6 w-40 px-2 rounded-full border border-dashed border-tb-navy/30 font-hand text-[11px] text-tb-navy" />
      {hits.length > 0 && (
        <div className="absolute z-20 top-full left-0 mt-1 w-64 rounded-lg border border-tb-navy/15 bg-white shadow-lg max-h-40 overflow-y-auto"
             data-testid={`atlas-ingest-target-add-hits-${candidateId}`}>
          {hits.map((h) => (
            <button key={h.slug} type="button"
              onClick={() => { onAdd(h.slug); setQ(""); setHits([]); }}
              data-testid={`atlas-ingest-target-add-hit-${candidateId}-${h.slug}`}
              className="w-full text-left px-2 py-1.5 font-hand text-xs hover:bg-tb-navy/5">
              <span className="text-tb-navy">{h.name}</span>
              <span className="text-tb-navy/40 ml-1">({h.slug})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// K4 — Conflicts admin page. Lists every fact pair currently
// flagged as `has_conflict` and lets the admin resolve each with
// one of four actions.
// ─────────────────────────────────────────────────────────────
function ConflictsPage() {
  const nav = useNavigate();
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // {fact_id, action, label}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/atlas/ingest/conflicts");
      setConflicts(data?.conflicts || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (fact_id, action) => {
    setBusyId(fact_id);
    try {
      const { data } = await api.post(
        `/atlas/ingest/conflicts/${fact_id}/resolve`, { action });
      toast.success(`Resolved · ${data.action}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setBusyId(null); setConfirmModal(null); }
  };

  const askResolve = (fact_id, action, label, destructive) => {
    if (destructive) setConfirmModal({ fact_id, action, label });
    else resolve(fact_id, action);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="atlas-ingest-conflicts-page">
      <button type="button" onClick={() => nav("/atlas/admin/ingest")}
        className="inline-flex items-center gap-1 text-tb-navy/60 hover:text-tb-navy text-xs font-hand mb-4"
        data-testid="atlas-ingest-conflicts-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to uploads
      </button>
      <h1 className="text-tb-navy text-3xl mb-1"
          style={{ fontFamily: '"Playfair Display", serif' }}>
        Ingestion conflicts
      </h1>
      <p className="text-tb-navy/60 font-hand text-sm mb-6">
        Facts where an ingestion tried to write a value that clashes with an existing
        canonical fact. Resolve to unblock the tree.
      </p>

      {loading && <div className="font-hand text-tb-navy/50">Loading…</div>}
      {!loading && conflicts.length === 0 && (
        <div className="rounded-xl border border-dashed border-tb-navy/20 p-8 text-center font-hand text-tb-navy/50 text-sm"
             data-testid="atlas-ingest-conflicts-empty">
          No open conflicts. Nothing to resolve.
        </div>
      )}

      <div className="space-y-3">
        {conflicts.map((c) => (
          <div key={c.fact_id}
               data-testid={`atlas-ingest-conflict-row-${c.fact_id}`}
               className="rounded-xl border border-amber-300 bg-amber-50/40 p-4">
            <div className="flex items-baseline gap-2 flex-wrap">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <span className="text-tb-navy font-semibold text-sm"
                    style={{ fontFamily: '"Playfair Display", serif' }}>
                {c.topic_name || c.topic_slug}
              </span>
              <span className="text-[10px] font-hand text-tb-navy/50 px-2 py-0.5 rounded-full border border-tb-navy/15">
                {c.attribute_key}
              </span>
              <span className="ml-auto text-[10px] font-hand text-tb-navy/40">
                {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <ConflictSide title="Existing (canonical)" tone="navy"
                value={c.existing_value}
                metadata={c.existing_metadata}
                testId={`atlas-ingest-conflict-existing-${c.fact_id}`} />
              <ConflictSide title="Incoming (ingested)" tone="amber"
                value={c.incoming_value}
                metadata={c.incoming_metadata}
                testId={`atlas-ingest-conflict-incoming-${c.fact_id}`} />
            </div>

            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              <button type="button"
                disabled={busyId === c.fact_id}
                onClick={() => askResolve(c.fact_id, "keep_old", "Keep the existing value?", false)}
                data-testid={`atlas-ingest-conflict-keep_old-${c.fact_id}`}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50">
                <CheckIcon className="w-3 h-3" /> Keep old
              </button>
              <button type="button"
                disabled={busyId === c.fact_id}
                onClick={() => askResolve(c.fact_id, "overwrite",
                  `Replace existing (${c.existing_value}) with incoming (${c.incoming_value})?`, true)}
                data-testid={`atlas-ingest-conflict-overwrite-${c.fact_id}`}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-50 border border-red-300 text-red-800 font-hand text-xs disabled:opacity-50">
                <RefreshCw className="w-3 h-3" /> Overwrite
              </button>
              <button type="button"
                disabled={busyId === c.fact_id}
                onClick={() => askResolve(c.fact_id, "store_both", "Keep both values?", false)}
                data-testid={`atlas-ingest-conflict-store_both-${c.fact_id}`}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-tb-navy/5 border border-tb-navy/20 text-tb-navy font-hand text-xs disabled:opacity-50">
                Store both
              </button>
              <button type="button"
                disabled={busyId === c.fact_id}
                onClick={() => askResolve(c.fact_id, "reject_new",
                  "Reject the incoming value?", true)}
                data-testid={`atlas-ingest-conflict-reject_new-${c.fact_id}`}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-tb-navy/5 border border-tb-navy/20 text-tb-navy font-hand text-xs disabled:opacity-50">
                <CloseIcon className="w-3 h-3" /> Reject new
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirmModal && (
        <div className="fixed inset-0 bg-tb-navy/50 flex items-center justify-center p-4 z-50"
             data-testid="atlas-ingest-conflict-confirm-modal"
             onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="text-tb-navy text-lg font-semibold"
                 style={{ fontFamily: '"Playfair Display", serif' }}>
              Confirm destructive action
            </div>
            <p className="mt-2 font-hand text-tb-navy/70 text-sm">
              {confirmModal.label}
            </p>
            <div className="mt-4 flex items-center gap-2 justify-end">
              <button type="button" onClick={() => setConfirmModal(null)}
                className="h-9 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-sm"
                data-testid="atlas-ingest-conflict-confirm-cancel">
                Cancel
              </button>
              <button type="button"
                onClick={() => resolve(confirmModal.fact_id, confirmModal.action)}
                className="h-9 px-4 rounded-full bg-red-600 text-white font-hand text-sm"
                data-testid="atlas-ingest-conflict-confirm-ok">
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConflictSide({ title, tone, value, metadata, testId }) {
  const border = tone === "amber" ? "border-amber-300 bg-amber-50" : "border-tb-navy/15 bg-white";
  return (
    <div className={`rounded-lg border ${border} p-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">
        {title}
      </div>
      <div className="mt-1 text-lg font-semibold text-tb-navy" data-testid={`${testId}-value`}>
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </div>
      <div className="mt-2 space-y-0.5 font-hand text-[11px] text-tb-navy/60">
        {metadata?.source_document && (
          <div><span className="text-tb-navy/40">Document:</span> {metadata.source_document}</div>
        )}
        {metadata?.source_supplier && (
          <div><span className="text-tb-navy/40">Supplier:</span> {metadata.source_supplier}</div>
        )}
        {metadata?.source_upload_date && (
          <div><span className="text-tb-navy/40">Uploaded:</span>{" "}
            {new Date(metadata.source_upload_date).toLocaleDateString()}
          </div>
        )}
        {metadata?.confidence_score != null && (
          <div><span className="text-tb-navy/40">Confidence:</span>{" "}
            {Math.round((metadata.confidence_score || 0) * 100)}%
          </div>
        )}
        {metadata?.provenance && (
          <div><span className="text-tb-navy/40">Provenance:</span> {metadata.provenance}</div>
        )}
      </div>
    </div>
  );
}

